import React, { useEffect, useRef, useState } from 'react';

import Toast from './Toast';

// Instagram tidak punya URL share untuk web. Pendekatan paling dekat:
// buat kartu gambar 1080x1920 (story) via canvas, lalu kirim lewat Web Share API (mobile)
// sehingga user bisa pilih Instagram > Feed / Story. Konten utama ditaruh di area tengah 4:5
// agar tetap utuh saat di-crop untuk Feed. Caption + link disalin ke clipboard untuk di-paste.
// Fallback (desktop): unduh kartu, salin caption & buka instagram.com.
// Opsi lain: kirim link saja via share sheet agar bisa dikirim ke Direct Message.
const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1920;
const FEED_TOP = (CARD_HEIGHT - CARD_WIDTH * 1.25) / 2;
const FEED_BOTTOM = CARD_HEIGHT - FEED_TOP;
const PADDING = 60;

const stripHtml = (html) => {
  const text = (html || '').replace(/(<([^>]+)>)/gi, '');
  if (typeof DOMParser === 'undefined') return text;
  return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent || text;
};

// Gambar artikel dari storage (webp) di-fetch sebagai blob agar canvas tidak ter-taint
const loadImage = async (src) => {
  const response = await fetch(src, { mode: 'cors' });
  if (!response.ok) throw new Error('Failed to load image');
  const objectUrl = URL.createObjectURL(await response.blob());
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = reject;
    img.src = objectUrl;
  });
};

const wrapLines = (ctx, text, maxWidth, maxLines) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    lines.length = maxLines;
    lines[maxLines - 1] = `${lines[maxLines - 1].replace(/\s+\S*$/, '')}...`;
  }
  return lines;
};

const drawCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
};

const createCardFile = async ({ title, url, image }) => {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  const [logo, photo] = await Promise.all([loadImage('/assets/img/logo.png').catch(() => null), image ? loadImage(image).catch(() => null) : null]);

  if (logo) {
    const logoWidth = 420;
    const logoHeight = (logo.height / logo.width) * logoWidth;
    ctx.drawImage(logo, (CARD_WIDTH - logoWidth) / 2, (FEED_TOP - logoHeight) / 2, logoWidth, logoHeight);
  }

  let cursorY = FEED_TOP + PADDING;
  if (photo) {
    const photoHeight = ((CARD_WIDTH - PADDING * 2) * 9) / 16;
    drawCover(ctx, photo, PADDING, cursorY, CARD_WIDTH - PADDING * 2, photoHeight);
    cursorY += photoHeight + PADDING;
  }

  ctx.fillStyle = '#CC0000';
  ctx.fillRect(PADDING, cursorY, 120, 10);
  cursorY += 50;

  ctx.fillStyle = '#222222';
  ctx.font = 'bold 64px Helvetica, Arial, sans-serif';
  ctx.textBaseline = 'top';
  const lineHeight = 80;
  const maxLines = Math.floor((FEED_BOTTOM - PADDING - cursorY) / lineHeight);
  wrapLines(ctx, title, CARD_WIDTH - PADDING * 2, maxLines).forEach((line, index) => {
    ctx.fillText(line, PADDING, cursorY + index * lineHeight);
  });

  ctx.fillStyle = '#CC0000';
  ctx.font = 'bold 40px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Baca selengkapnya di', CARD_WIDTH / 2, FEED_BOTTOM + 70);
  ctx.fillStyle = '#555555';
  ctx.font = '36px Helvetica, Arial, sans-serif';
  const shortUrl = url.replace(/^https?:\/\//, '');
  ctx.fillText(shortUrl.length > 48 ? `${shortUrl.slice(0, 45)}...` : shortUrl, CARD_WIDTH / 2, FEED_BOTTOM + 130);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error('Failed to create image');
  return new File([blob], 'ivoox-instagram.jpg', { type: 'image/jpeg' });
};

const InstagramShareButton = ({ url, title, image, size = 38 }) => {
  const fileRef = useRef(null);
  const plainTitle = stripHtml(title);
  const caption = `${plainTitle}\n\nBaca selengkapnya: ${url}`;

  // Kartu disiapkan lebih awal, karena Safari menolak navigator.share bila ada await panjang setelah klik
  useEffect(() => {
    let cancelled = false;
    fileRef.current = null;
    createCardFile({ title: plainTitle, url, image })
      .then((file) => {
        if (!cancelled) fileRef.current = file;
      })
      .catch((error) => console.error(error));
    return () => {
      cancelled = true;
    };
  }, [plainTitle, url, image]);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('touchstart', onOutsideClick);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('touchstart', onOutsideClick);
    };
  }, [open]);

  const copyText = (text) => {
    if (navigator.clipboard) return navigator.clipboard.writeText(text).catch(() => {});
    return Promise.resolve();
  };

  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Kirim link saja, Instagram muncul di share sheet sebagai tujuan Direct / Chats
  const onShareLink = async () => {
    setOpen(false);
    if (navigator.share) {
      try {
        await navigator.share({ title: plainTitle, text: plainTitle, url });
        return;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
    }

    await copyText(url);
    Toast('Link copied, paste it in Instagram Direct', 'success');
    window.open('https://www.instagram.com/direct/inbox/', '_blank', 'noopener,noreferrer');
  };

  const onShareImage = async () => {
    setOpen(false);
    const file = fileRef.current;
    copyText(caption);

    if (navigator.share) {
      try {
        // Hanya kirim file (tanpa text) agar Instagram muncul di share sheet dengan opsi Feed / Story
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
          Toast('Caption copied, paste it in Instagram', 'success');
          await navigator.share({ files: [file] });
          return;
        }
        await navigator.share({ title: plainTitle, text: caption, url });
        return;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
    }

    if (file) downloadFile(file);
    Toast(file ? 'Image downloaded & caption copied, upload it to Instagram' : 'Caption & link copied, paste it in your Instagram post', 'success');
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };

  const menuItemStyle = {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    fontSize: 14,
    color: '#222',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type='button'
        aria-label='instagram'
        aria-haspopup='menu'
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer'
        }}
      >
        <svg viewBox='0 0 64 64' width={size} height={size}>
          <defs>
            <linearGradient id='ig-share-gradient' x1='0' y1='1' x2='1' y2='0'>
              <stop offset='0%' stopColor='#feda75' />
              <stop offset='30%' stopColor='#fa7e1e' />
              <stop offset='55%' stopColor='#d62976' />
              <stop offset='80%' stopColor='#962fbf' />
              <stop offset='100%' stopColor='#4f5bd5' />
            </linearGradient>
          </defs>
          <circle cx='32' cy='32' r='31' fill='url(#ig-share-gradient)' />
          <rect x='18' y='18' width='28' height='28' rx='8' fill='none' stroke='white' strokeWidth='3' />
          <circle cx='32' cy='32' r='7' fill='none' stroke='white' strokeWidth='3' />
          <circle cx='40.5' cy='23.5' r='2' fill='white' />
        </svg>
      </button>
      {open && (
        <div
          role='menu'
          style={{
            position: 'absolute',
            top: size + 6,
            // Di mobile tombol share berjajar horizontal, jadi menu dibuka ke kiri agar tidak keluar layar
            ...(typeof window !== 'undefined' && window.innerWidth < 480 ? { right: 0 } : { left: 0 }),
            zIndex: 1000,
            backgroundColor: '#fff',
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }}
        >
          <button type='button' role='menuitem' style={menuItemStyle} onClick={onShareLink}>
            Kirim link (Direct Message)
          </button>
          <button type='button' role='menuitem' style={{ ...menuItemStyle, borderTop: '1px solid #eee' }} onClick={onShareImage}>
            Bagikan ke Feed / Story
          </button>
        </div>
      )}
    </div>
  );
};

export default InstagramShareButton;
